import React, { useContext } from 'react';
import { useMyPortfolioCapsulesConfigSlice } from 'widgets/myPortfolioCapsules/store/config/configSlice';
import { useMyPortfolioCapsuleData } from 'widgets/myPortfolioCapsules/hooks/useMyPortfolioCapsuleData';
import { MyPortfolioCapsulesCapsule } from './capsule/MyPortfolioCapsulesCapsule';

import './MyPortfolioCapsulesWidgetContent.scss';
import 'widgets/tiles/components/styles/styles.scss';
import classNames from 'classnames';
import { useMyPortfolioCapsuleStreams } from 'widgets/myPortfolioCapsules/hooks/useMyPortfolioCapsuleStreams';
import { useWidgetContext } from 'widgets/WidgetContext';
import { textUtils } from '@instinet-analytics/core-lib';
import { IconTooltip } from 'utils/components/IconTooltip';
import { getTopic } from 'widgets/myPortfolioCapsules/topics/Topic';
import { useMyPortfolioCapsuleWidgetStrategy } from 'widgets/myPortfolioCapsules/hooks/useMyPortfolioCapsuleWidgetStrategies';
import { SortDirection } from '@instinet-analytics/core-lib';
import AppContext from 'app/AppContext';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import { MyPortfolioCapsulesWidgetConfigState } from '../store/config/MyPortfolioCapsulesWidgetConfigState';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';

const { pluralise } = textUtils;

export function MyPortfolioCapsulesWidgetContent() {
    const { height } = useWidgetContext();
    const isLocked = useSelectLocked();
    const { config, dispatchConfigChanged } =
        useMyPortfolioCapsulesConfigSlice();
    const appContext = useContext(AppContext);
    const { symbology } = useSelectAppSettings();
    const {
        topic: topicType,
        orientation,
        hideToolbar,
        capsuleType,
        sort,
        showTitleCapsule,
    } = config;
    const data = useMyPortfolioCapsuleData();

    const topic = getTopic(topicType!);

    useMyPortfolioCapsuleStreams(data);

    const strategy = useMyPortfolioCapsuleWidgetStrategy(capsuleType!);
    const { title: capsuleTitle, description: capsuleDescription } = strategy;

    if (data.length === 0) {
        return (
            <div className="MyPortfolioCapsulesWidgetContent no-data">
                No Data
            </div>
        );
    }

    const tilesHeight = hideToolbar ? height : height - 28 - 18;
    const style = { height: tilesHeight };

    const containerCssClasses = classNames(
        'capsules-container',
        `orientation-${orientation}`
    );

    const tooltipTitleStyle = { fontWeight: 700, marginBottom: 5 };

    const tooltip = (
        <div>
            <div style={tooltipTitleStyle}>{capsuleTitle} Capsule</div>
            {capsuleDescription?.body}
            <div style={{ ...tooltipTitleStyle, marginTop: 12 }}>
                {topic.description} Topic
            </div>
            {topic.help}
        </div>
    );

    const topicDescription = topic.getDescription
        ? topic.getDescription(appContext, config)
        : topic.description;

    const handleCloseClick = () => {
        const delta: Partial<MyPortfolioCapsulesWidgetConfigState> = {
            showTitleCapsule: false,
        };
        dispatchConfigChanged(delta);
    };

    const titleCapsule = (
        <div className="header">
            <div className="titles capsule">
                <div className="panel-header">
                    <div className="title capsule-title">
                        {pluralise(1, capsuleTitle)} Capsules
                    </div>
                    <div className="toolbar">
                        <IconTooltip maxWidth={310}>{tooltip}</IconTooltip>
                        {!isLocked && (
                            <Icon name="close" onClick={handleCloseClick} />
                        )}
                    </div>
                </div>
                <div className="title">
                    Ordered by {topicDescription}
                    {sort === SortDirection.ASCENDING ? ' (Ascending)' : ''}
                </div>
            </div>
        </div>
    );

    return (
        <div className="MyPortfolioCapsulesWidgetContent">
            <div className={containerCssClasses}>
                {showTitleCapsule && orientation === 'row' && titleCapsule}
            </div>
            <div className={containerCssClasses} style={style}>
                {showTitleCapsule && orientation === 'column' && titleCapsule}
                {data.map((d, i) => (
                    <MyPortfolioCapsulesCapsule
                        key={`${d.symbol}_${capsuleType}_${i}`}
                        symbol={d.symbol}
                        qty={d.qty}
                        qtyRemaining={d.qtyRemaining}
                        value={d.value}
                        config={config}
                        strategy={strategy}
                        symbology={symbology}
                    />
                ))}
            </div>
        </div>
    );
}
